import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import i18nInst from '@shared/i18n';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { Skeleton, SectionErrorBoundary, VerifiedBadge } from '@shared/ui';
import Avatar from '@shared/ui/Avatar';
import { NamedIcon } from '@shared/ui/icons';
import { useBodyScrollLock } from '@shared/hooks/useBodyScrollLock';
import { useDialogFocus } from '@shared/hooks/useDialogFocus';
import { useHandleVerification } from '@shared/hooks/useHandleVerification';
import CollabSection, { type OpenPartner } from './CollabSection';
import { SignedImg, SignedVideo } from '@shared/ui/SignedMedia';
import PostViewerModal from '@shared/ui/PostViewerModal';
import { useShortlist } from './shortlistStore';
import { IgLink } from '@shared/ui/IgLink';
import { currentLocale } from '@shared/i18n';

// Shared-theme Nivo versions of the creator-card charts (lazy → Nivo ships on demand).
const CreatorAudienceSparklines = lazy(() => import('./CreatorChartsNivo').then((m) => ({ default: m.CreatorAudienceSparklines })));
const CreatorFrequencyNivo = lazy(() => import('./CreatorChartsNivo').then((m) => ({ default: m.CreatorFrequencyNivo })));
const CreatorScatterNivo = lazy(() => import('./CreatorChartsNivo').then((m) => ({ default: m.CreatorScatterNivo })));

interface PostRow {
  id: string;
  post_type: string | null;
  posted_at: string | null;
  thumb_public_url: string | null;
  thumbnail_url: string | null;
  video_public_url: string | null;
  permalink: string | null;
  likes: number | null;
  comments: number | null;
  views: number | null;
  is_paid_partnership: boolean | null;
  caption: string | null;
  is_coauthored?: boolean | null;
  owner_handle?: string | null;
  is_pinned?: boolean | null;
  coauthors?: Array<unknown> | null;
  brands?: Array<{ name: string; ocr: boolean }> | null;
  // { slides: [{url, public_url, mirrored_at}] } — PostViewerModal pages through these.
  child_posts?: unknown;
}

interface StoryRow {
  id: string;
  taken_at: string | null;
  media_url: string | null;
  thumbnail_url: string | null;
  thumb_public_url: string | null;
  media_type: string | null;
}

interface SnapshotRow {
  scraped_date: string;
  followers: number | null;
  following: number | null;
  posts_count: number | null;
  avg_likes: number | null;
  avg_comments: number | null;
  engagement_rate: number | null;
}

interface SnapshotHistoryRow {
  scraped_date: string;
  followers: number | null;
  engagement_rate: number | null;
  avg_likes: number | null;
  avg_comments: number | null;
}

interface WeekBucket {
  label: string;
  date: string;
  organic: number;
  branded: number;
  avgLikes: number | null;
}

interface CreatorCard {
  bio: string | null;
  avatar_url: string | null;
  ig_category_name: string | null;
  is_business: boolean | null;
  content_languages: string[];
  niches: string[];
  ambassador_brands: { id: string; name: string | null }[];
  ambassador_handles_raw: string[];
  brand_mentions: { brand_id: string; name: string | null; count: number; avatar_url: string | null }[];
}

// get_creator_offer — what a business needs to decide and get in touch.
interface CreatorOffer {
  full_name: string | null;
  city: string | null;
  accepts_barter: boolean;
  prices: Partial<Record<'reel' | 'story' | 'post', { price: number | string; currency: string | null; at: string | null }>>;
  contacts: { whatsapp?: string; telegram?: string; phone?: string; email?: string };
  band: { lo: number; hi: number | null; median_er: number | string | null; n: number } | null;
}

type CardTab = 'overview' | 'brands' | 'posts' | 'stories' | 'network';

const median = (xs: number[]): number | null => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

const bandLabel = (lo: number, hi: number | null): string => {
  const k = (v: number) => (v >= 1000 ? `${v / 1000}K` : String(v));
  if (hi == null) return `${k(lo)}+`;
  if (lo === 0) return `<${k(hi)}`;
  return `${k(lo)}–${k(hi)}`;
};

// WhatsApp wants bare digits; Telegram a username without @ or a t.me link as-is.
const waHref = (v: string) => `https://wa.me/${v.replace(/\D/g, '')}`;
const tgHref = (v: string) => (/^https?:\/\//i.test(v) ? v : `https://t.me/${v.replace(/^@+/, '').replace(/^t\.me\//i, '')}`);

interface Props {
  handle: string;
  instagramProviderId: string;
  followers: number;
  engagement: number;
  onClose: () => void;
  onOpenCreator?: (p: OpenPartner) => void;
}

const compact = (v: number | null | undefined): string => {
  if (v == null) return '—';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString(i18nInst.language);
};

const fmtDate = (s: string | null) => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString(currentLocale(), { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtShort = (s: string | null) =>
  s ? new Date(s).toLocaleDateString(currentLocale(), { day: 'numeric', month: 'short' }) : '';

const DAYS = 60;

// Local-timezone YYYY-MM-DD. MUST match how getDailyBuckets keys its buckets:
// the bars are bucketed by local-midnight boundaries, so the grid filter has to
// compare on the same local day — .toISOString().slice(0,10) is UTC and shifts a
// day for users east of UTC (e.g. Bishkek +6), filtering the grid to the wrong day.
const localDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function getDailyBuckets(
  items: { date: string | null; hasMention: boolean; likes?: number | null }[],
): WeekBucket[] {
  const now = new Date();
  now.setHours(23, 59, 59, 999);
  const buckets = Array.from({ length: DAYS }, (_, i) => {
    const start = new Date(now);
    start.setDate(now.getDate() - (DAYS - 1 - i));
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 1);
    return { start, end, label: fmtShort(start.toISOString()), organic: 0, branded: 0, likesSum: 0, likesCount: 0 };
  });
  for (const item of items) {
    if (!item.date) continue;
    const d = new Date(item.date);
    for (let i = buckets.length - 1; i >= 0; i--) {
      if (d >= buckets[i].start && d < buckets[i].end) {
        item.hasMention ? buckets[i].branded++ : buckets[i].organic++;
        if (item.likes != null) { buckets[i].likesSum += item.likes; buckets[i].likesCount++; }
        break;
      }
    }
  }
  return buckets.map(({ start, label, organic, branded, likesSum, likesCount }) => ({
    label, date: localDay(start), organic, branded,
    avgLikes: likesCount > 0 ? Math.round(likesSum / likesCount) : null,
  }));
}

interface TrendPoint { date: string; followers: number | null; er: number | null }

const CHART_LABELS = (tr: (k: string, o?: Record<string, unknown>) => string) => ({
  audience: { followers: tr('social.creator.legend.followers'), er: tr('social.creator.legend.er'), deltaPct: '%', deltaPp: tr('social.creator.chart.ppWord', { defaultValue: ' п.п.' }) },
  freq: { organic: tr('social.creator.legend.organic'), brand: tr('social.creator.legend.brand'), avgLikes: tr('social.creator.legend.avgLikes'), posts: tr('social.creator.chart.postsWord') },
  stories: { organic: tr('social.creator.legend.organic'), brand: tr('social.creator.legend.brand'), avgLikes: '', posts: tr('social.creator.chart.storiesWord', { defaultValue: 'сторис' }) },
  scatter: { reelVideo: tr('social.creator.legend.reelVideo'), carousel: tr('social.creator.legend.carousel'), photo: tr('social.creator.legend.photo'), likes: tr('social.creator.chart.likesWord'), er: tr('social.creator.chart.erWord', { defaultValue: 'ER' }), views: tr('social.creator.chart.viewsWord', { defaultValue: 'просмотры' }), sponsor: tr('social.creator.legend.brand'), clickHint: tr('social.creator.chart.clickHint', { defaultValue: 'нажмите точку — фильтр публикаций' }) },
});

export default function CreatorPreviewModal({ handle, instagramProviderId, followers, engagement, onClose, onOpenCreator }: Props) {
  // Intentionally NOT auto-closing on tab switch: customers open IG profiles / post
  // permalinks in new tabs and expect the card to still be here when they come back.
  // (The stale-backdrop freeze that hook guarded against is handled by useBodyScrollLock.)
  const { t: tr, i18n } = useTranslation('customer');
  const lang = i18n.language || 'ru';
  const chartLabels = CHART_LABELS(tr);
  const verif = useHandleVerification([handle]);
  useBodyScrollLock();
  const dialogRef = useDialogFocus<HTMLDivElement>();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Owned + co-authored posts (one canonical row shown on every participant's grid).
  const postsQ = useSupabaseQuery<PostRow[]>(
    ['creator-posts', instagramProviderId],
    // 400, not 90. The grid shows the creator's full fetched history (no recency
    // window), so the server limit has to reach back far — and for a newsroom 90 is
    // nowhere near enough: akipress publishes ~12 posts a day, so 90 rows cover barely
    // a week and every older post, including the ones carrying banner ads, was invisible
    // in the card while sitting in the database. Thumbnails load lazily, so the extra
    // rows cost nothing until they scroll into view.
    async () => supabase.rpc('get_creator_grid_posts', { p_provider_id: instagramProviderId, p_limit: 400 }),
  );

  const storiesQ = useSupabaseQuery<StoryRow[]>(
    ['creator-stories', instagramProviderId],
    async () =>
      supabase
        .from('ig_stories')
        .select('id, taken_at, media_url, thumbnail_url, thumb_public_url, media_type')
        .eq('instagram_provider_id', instagramProviderId)
        .order('taken_at', { ascending: false })
        .limit(90),
  );

  const snapshotQ = useSupabaseQuery<SnapshotRow[]>(
    ['creator-snapshot', instagramProviderId],
    async () =>
      supabase
        .from('ig_profile_snapshots')
        .select('scraped_date, followers, following, posts_count, avg_likes, avg_comments, engagement_rate')
        .eq('instagram_provider_id', instagramProviderId)
        .order('scraped_date', { ascending: false })
        .limit(1),
  );

  const historyQ = useSupabaseQuery<SnapshotHistoryRow[]>(
    ['creator-snapshot-history', instagramProviderId],
    async () => supabase.rpc('get_creator_snapshot_history', { p_provider_id: instagramProviderId }),
  );

  const trendPoints = useMemo<TrendPoint[]>(
    () =>
      (historyQ.data ?? []).map((r) => ({
        date: r.scraped_date,
        followers: r.followers,
        er: r.engagement_rate != null ? Number(r.engagement_rate) : null,
      })),
    [historyQ.data],
  );

  const snapshot = snapshotQ.data?.[0] ?? null;

  // Authenticity heuristic from the follower-snapshot history: a sharp jump between
  // consecutive scrapes (or a spike that then reverts) suggests bought followers.
  // Cheap, honest, framed as "possible — verify", and only for accounts big enough
  // that % swings aren't just small-number noise.
  const authFlag = useMemo(() => {
    const pts = trendPoints.filter((p) => p.followers != null && p.followers > 0) as { date: string; followers: number }[];
    const base = snapshot?.followers ?? followers ?? 0;
    if (pts.length < 3 || base < 3000) return null;
    let maxJump = 0;
    for (let i = 1; i < pts.length; i++) {
      const days = Math.max(1, (new Date(pts[i].date).getTime() - new Date(pts[i - 1].date).getTime()) / 86400000);
      if (days > 45) continue; // ignore long gaps between scrapes
      maxJump = Math.max(maxJump, (pts[i].followers - pts[i - 1].followers) / pts[i - 1].followers);
    }
    for (let i = 2; i < pts.length; i++) {
      const a = pts[i - 2].followers, b = pts[i - 1].followers, c = pts[i].followers;
      if ((b - a) / a > 0.2 && (c - b) / b < -0.05) return { level: 'high' as const, pct: 0 };
    }
    if (maxJump >= 0.3) return { level: 'watch' as const, pct: Math.round(maxJump * 100) };
    return null;
  }, [trendPoints, snapshot, followers]);

  const posts = postsQ.data ?? [];
  // Stats (cadence chart, brand-mention %) count only posts this creator OWNS,
  // so a co-author's reach doesn't inflate them. The grid still shows both.
  const ownedPosts = posts.filter((p) => !p.is_coauthored);
  const stories = storiesQ.data ?? [];

  const postMentionsQ = useSupabaseQuery<{ post_id: string }[]>(
    ['creator-post-mentions', instagramProviderId],
    async () =>
      supabase
        .from('ig_post_brand_mentions')
        .select('post_id')
        .in('post_id', posts.map(p => p.id)),
    { enabled: posts.length > 0 },
  );

  const storyMentionsQ = useSupabaseQuery<{ story_id: string; brands: { name: string | null } | null }[]>(
    ['creator-story-mentions', instagramProviderId],
    async () =>
      supabase
        .from('ig_story_brand_mentions')
        .select('story_id, brands(name)')
        .in('story_id', stories.map(s => s.id)),
    { enabled: stories.length > 0 },
  );

  // Profile card: bio, niches, languages, ambassador brands, brand-mention counts —
  // bundled in one SECURITY DEFINER RPC (mirrors the admin creator card).
  const cardQ = useSupabaseQuery<CreatorCard>(
    ['creator-card', instagramProviderId],
    async () => supabase.rpc('get_creator_card', { p_provider_id: instagramProviderId }),
  );
  const card = cardQ.data ?? null;

  const brandedPostIds = useMemo(
    () => new Set((postMentionsQ.data ?? []).map(r => r.post_id)),
    [postMentionsQ.data],
  );
  const brandedStoryIds = useMemo(
    () => new Set((storyMentionsQ.data ?? []).map(r => r.story_id)),
    [storyMentionsQ.data],
  );
  // story_id → distinct brand names mentioned in that story (for the badge + viewer)
  const storyBrands = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const r of storyMentionsQ.data ?? []) {
      const nm = r.brands?.name;
      if (!nm) continue;
      const arr = m.get(r.story_id) ?? [];
      if (!arr.includes(nm)) arr.push(nm);
      m.set(r.story_id, arr);
    }
    return m;
  }, [storyMentionsQ.data]);

  const offerQ = useSupabaseQuery<CreatorOffer>(
    ['creator-offer', instagramProviderId],
    async () => supabase.rpc('get_creator_offer', { p_provider_id: instagramProviderId }),
  );
  const offer = offerQ.data ?? null;

  const [tab, setTab] = useState<CardTab>('overview');
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [priceFmt, setPriceFmt] = useState<'reel' | 'story' | 'post' | null>(null);
  const [quoted, setQuoted] = useState('');
  const [postKind, setPostKind] = useState<'all' | 'own' | 'collab'>('all'); // posts sub-filter: own vs collab
  const [postFilter, setPostFilter] = useState<Set<string>>(new Set());   // post ids picked from the scatter
  const [brandFilter, setBrandFilter] = useState<Set<string>>(new Set());  // brand names picked from the mentions rail
  const [storyFilter, setStoryFilter] = useState<Set<string>>(new Set());  // day dates picked from the stories bars
  const [viewerStory, setViewerStory] = useState<StoryRow | null>(null);   // story open in the fullscreen viewer
  const [viewerPost, setViewerPost] = useState<PostRow | null>(null);      // post open in the in-app viewer (not IG)
  const toggle = (setter: React.Dispatch<React.SetStateAction<Set<string>>>) => (v: string) =>
    setter((prev) => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n; });

  const { has: inShortlist, toggle: toggleShortlist } = useShortlist();

  const postBuckets = useMemo(
    () => getDailyBuckets(
      ownedPosts.map(p => ({ date: p.posted_at, hasMention: brandedPostIds.has(p.id), likes: p.likes })),
    ),
    [ownedPosts, brandedPostIds],
  );

  // Story cadence comes from get_creator_story_activity (per-day counts over the
  // FULL history), not the last-90 preload — otherwise the 60-day axis was almost
  // all empty and everything clumped in the last few days for heavy posters. We
  // scaffold DAYS continuous buckets (zeros for silent days) and fill from the RPC.
  // Both sides key days in Asia/Bishkek (the business tz, no DST) so the join is
  // tz-stable regardless of the viewer's browser timezone.
  const storyActivityQ = useSupabaseQuery<{ d: string; organic: number; branded: number }[]>(
    ['creator-story-activity', instagramProviderId],
    async () => supabase.rpc('get_creator_story_activity', { p_provider_id: instagramProviderId, p_days: DAYS }),
  );
  const storyBuckets = useMemo(
    () => {
      const byDay = new Map<string, { organic: number; branded: number }>();
      for (const r of storyActivityQ.data ?? []) byDay.set(r.d, { organic: r.organic, branded: r.branded });
      const bishkekDay = (d: Date) =>
        new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bishkek', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
      const anchor = new Date();
      anchor.setHours(12, 0, 0, 0); // midday anchor avoids day-boundary jitter when stepping back
      return Array.from({ length: DAYS }, (_, i): WeekBucket => {
        const day = new Date(anchor);
        day.setDate(anchor.getDate() - (DAYS - 1 - i));
        const key = bishkekDay(day);
        const v = byDay.get(key);
        return { label: fmtShort(day.toISOString()), date: key, organic: v?.organic ?? 0, branded: v?.branded ?? 0, avgLikes: null };
      });
    },
    [storyActivityQ.data],
  );

  // The grid shows the creator's FULL fetched history (no recency window). A 60-day
  // cut used to strand the collab sub-tab on a handful of posts (4 of 21 on withomka)
  // — the data is already client-side (RPC returns up to 400) and thumbnails are
  // lazy-loaded, so there's no cost to showing everything. The chart below still uses
  // the DAYS window; that's a trend visualization, not the browsable list.
  const gridPosts = posts;
  const pinnedCount = posts.filter(p => p.is_pinned).length;
  // A post is a collaboration if the creator is a co-author (owner is someone else) OR the
  // creator OWNS it but credited co-authors (e.g. a brand collab reel). Both appear on their feed.
  const isCollab = (p: PostRow) => !!p.is_coauthored || (Array.isArray(p.coauthors) && p.coauthors.length > 0);
  const ownCount = gridPosts.filter((p) => !isCollab(p)).length;
  const collabCount = gridPosts.length - ownCount;
  const kindPosts = postKind === 'own' ? gridPosts.filter((p) => !isCollab(p))
    : postKind === 'collab' ? gridPosts.filter(isCollab) : gridPosts;
  // Brand picked from the mentions rail searches ALL posts, not just the recent
  // window — the whole point is to find when this creator worked with that brand.
  const brandPosts = useMemo(
    () => (brandFilter.size === 0
      ? null
      : posts.filter(p => (p.brands ?? []).some(b => brandFilter.has(b.name)))),
    [posts, brandFilter],
  );
  // What the rail claims for the picked brands — mentions over posts AND stories,
  // counted by provider link. The grid can only show owned posts, so the two
  // disagree and the chip has to say so.
  const brandFilteredTotal = useMemo(
    () => (cardQ.data?.brand_mentions ?? [])
      .filter(m => m.name && brandFilter.has(m.name))
      .reduce((s, m) => s + (m.count ?? 0), 0),
    [cardQ.data, brandFilter],
  );
  // Stories keep the 60-day window (unlike the posts grid): they expire on IG, so
  // old ones are noise, and the DAYS trend chart is anchored to the same span.
  const storyWindowStartIso = new Date(Date.now() - DAYS * 24 * 60 * 60 * 1000).toISOString();
  const recentStories = stories.filter(s => s.taken_at && s.taken_at >= storyWindowStartIso);
  // Picking a brand from the mentions rail filters the stories tab too. The modal
  // only preloads the last 90 stories (≈days for a heavy story-poster), so filtering
  // that window would miss most brand stories — withomka has 17 Tomiko stories but
  // only 1 in the last 90. So we fetch the brand's stories across the FULL history on
  // demand, using the same predicates as the rail count (get_creator_card) so the two
  // reconcile. Names → ids via the rail payload.
  const brandIdsForFilter = useMemo(
    () => (brandFilter.size === 0
      ? []
      : (card?.brand_mentions ?? []).filter(m => m.name && brandFilter.has(m.name)).map(m => m.brand_id)),
    [card, brandFilter],
  );
  const brandStoriesQ = useSupabaseQuery<Array<StoryRow & { brands: string[] | null }>>(
    ['creator-brand-stories', instagramProviderId, [...brandIdsForFilter].sort().join(',')],
    async () => supabase.rpc('get_creator_brand_stories', { p_provider_id: instagramProviderId, p_brand_ids: brandIdsForFilter }),
    { enabled: brandIdsForFilter.length > 0 },
  );
  const brandStories = brandFilter.size > 0 ? (brandStoriesQ.data ?? []) : null;
  // Badge source for brand-fetched stories (they fall outside the last-90 storyBrands map).
  const brandStoryBadges = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const r of brandStoriesQ.data ?? []) m.set(r.id, r.brands ?? []);
    return m;
  }, [brandStoriesQ.data]);

  // ── Headline numbers. Each tile carries the context that makes it readable. ──
  const fmtInt = (v: number) => Math.round(v).toLocaleString(currentLocale());
  const fmtPct = (v: number, d = 2) => `${v.toFixed(d).replace('.', lang === 'en' ? '.' : ',')}%`;
  const followersNow = snapshot?.followers ?? followers ?? null;
  const erNow = snapshot?.engagement_rate != null ? Number(snapshot.engagement_rate) : (engagement != null ? Number(engagement) : null);

  const growth = useMemo(() => {
    const pts = trendPoints.filter((p) => p.followers != null && p.followers > 0);
    if (pts.length < 2) return null;
    const a = pts[0], b = pts[pts.length - 1];
    return { pct: ((b.followers! - a.followers!) / a.followers!) * 100, since: a.date };
  }, [trendPoints]);

  const bandMed = offer?.band?.median_er != null ? Number(offer.band.median_er) : null;
  const erColor = erNow == null || bandMed == null ? '#dde'
    : erNow >= bandMed * 1.2 ? '#5fd17a' : erNow < bandMed * 0.7 ? '#ff8f8f' : '#dde';

  // Views = video_plays (what Instagram shows), median of the 12 latest own Reels —
  // the same window the ER canon uses.
  const reelViews = useMemo(() => {
    const reels = ownedPosts
      .filter((p) => (p.post_type === 'reel' || p.post_type === 'video') && p.views != null && p.views > 0)
      .sort((a, b) => (b.posted_at ?? '').localeCompare(a.posted_at ?? ''))
      .slice(0, 12);
    return reels.length >= 3 ? { med: median(reels.map((p) => p.views!))!, n: reels.length } : null;
  }, [ownedPosts]);

  const since90 = Date.now() - 90 * 86400000;
  const posts90 = ownedPosts.filter((p) => p.posted_at && new Date(p.posted_at).getTime() >= since90);
  const branded90 = posts90.filter((p) => (p.brands ?? []).length > 0).length;

  // Story count from the per-day activity RPC (full history), not the last-90 preload.
  const stories30 = useMemo(() => {
    if (!storyActivityQ.data) return null;
    const cut = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bishkek', year: 'numeric', month: '2-digit', day: '2-digit' })
      .format(new Date(Date.now() - 30 * 86400000));
    return storyActivityQ.data.filter((r) => r.d > cut).reduce((s, r) => s + r.organic + r.branded, 0);
  }, [storyActivityQ.data]);

  const lastPostAt = ownedPosts.reduce<string | null>((m, p) => (p.posted_at && (!m || p.posted_at > m) ? p.posted_at : m), null);

  // ── Price: the creator's own list, checked against what the business was quoted. ──
  const priceFormats = (['reel', 'story', 'post'] as const).filter((k) => offer?.prices?.[k]);
  const fmtKey = priceFmt && priceFormats.includes(priceFmt) ? priceFmt : priceFormats[0] ?? null;
  const listPrice = fmtKey ? Number(offer!.prices[fmtKey]!.price) : null;
  const quotedNum = Number(quoted.replace(/\s/g, ''));
  const verdict = (() => {
    if (!listPrice || !quoted.trim() || !(quotedNum > 0)) return null;
    const r = quotedNum / listPrice;
    if (r < 0.8) return { text: tr('social.creator.price.below', { pct: Math.round((1 - r) * 100) }), color: '#f5cf7a' };
    if (r <= 1.05) return { text: tr('social.creator.price.match'), color: '#5fd17a' };
    if (r <= 1.3) return { text: tr('social.creator.price.above', { pct: Math.round((r - 1) * 100), price: fmtInt(listPrice) }), color: '#f5cf7a' };
    return { text: tr('social.creator.price.over', { x: r.toFixed(1).replace('.', lang === 'en' ? '.' : ',') }), color: '#ff8f8f' };
  })();
  const cpm = fmtKey === 'reel' && listPrice && reelViews ? (listPrice / reelViews.med) * 1000 : null;

  const contacts = offer?.contacts ?? {};

  const statTile = (label: string, value: string, hint?: string | null, valueColor?: string, hintColor?: string) => (
    <div style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)', minWidth: 0 }}>
      <div style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)', color: valueColor ?? 'var(--text)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)', marginTop: 2 }}>{label}</div>
      {hint && <div style={{ fontSize: 'var(--fs-caption)', color: hintColor ?? 'var(--dim)', marginTop: 1 }}>{hint}</div>}
    </div>
  );
  const btn = (primary = false): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 44, padding: '0 14px',
    borderRadius: 10, fontFamily: 'inherit', fontSize: 'var(--fs-body)', fontWeight: primary ? 600 : 500, cursor: 'pointer',
    textDecoration: 'none', whiteSpace: 'nowrap',
    background: primary ? '#2563eb' : 'transparent', color: primary ? '#fff' : '#dde',
    border: primary ? '1px solid #2563eb' : '1px solid rgba(255,255,255,0.15)',
  });
  const chip = (key: string, text: string, c: string, upper = false) => (
    <span key={key} style={{ fontSize: 'var(--fs-caption)', padding: '3px 9px', borderRadius: 20, background: `${c}1a`, border: `1px solid ${c}44`, color: c, whiteSpace: 'nowrap', textTransform: upper ? 'uppercase' : 'none' }}>{text}</span>
  );
  const infoLabel = { fontSize: 'var(--fs-caption)', color: '#667', textTransform: 'uppercase' as const, letterSpacing: 0.4, marginBottom: 6 };
  const chipWrap = { display: 'flex', flexWrap: 'wrap' as const, gap: 6 };

  // Portal to <body>: rendered in place, the overlay sat inside a stacking context
  // and the sticky site header (z 30) painted over the top of the card.
  return createPortal(
    <>
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh clamp(8px, 3vw, 16px)', overflowY: 'auto', backdropFilter: 'blur(6px)' }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`@${handle}`}
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'relative', outline: 'none', width: '100%', maxWidth: 1100, background: 'var(--bg-elev, #161b22)', border: '1px solid var(--border-strong, #2a2d35)', borderRadius: 14, padding: 'clamp(14px, 4vw, 24px)', boxShadow: '0 24px 60px rgba(0,0,0,0.55)' }}
      >
        <button
          onClick={onClose}
          aria-label={tr('common.close', { defaultValue: 'Close' })}
          style={{ position: 'absolute', top: 16, right: 16, zIndex: 2, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'var(--text)', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 'var(--fs-label)', lineHeight: 1, fontFamily: 'inherit' }}
        >×</button>
        <SectionErrorBoundary name="creator-preview">
          {/* Header — the × is absolute (above), so reserve room on the right. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, paddingRight: 40 }}>
            <Avatar src={card?.avatar_url} name={handle} size={56} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 'clamp(17px, 5vw, var(--fs-h1))', fontWeight: 'var(--fw-bold)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', overflowWrap: 'anywhere' }}>
                {offer?.full_name ?? `@${handle}`}
                {offer?.full_name && <a href={`https://instagram.com/${handle}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 'var(--fs-body)', fontWeight: 400, color: 'var(--accent)', textDecoration: 'none' }}>@{handle}</a>}
                <VerifiedBadge {...verif(handle)} size={18} />
              </h2>
              <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)', marginTop: 3 }}>
                {[
                  (card?.niches ?? []).slice(0, 2).join(', '),
                  offer?.city ? tr(`social.creator.city.${offer.city}`, { defaultValue: offer.city }) : null,
                  (card?.content_languages ?? []).join(', '),
                  lastPostAt ? tr('social.creator.lastPost', { date: fmtShort(lastPostAt) }) : null,
                ].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>

          {/* Headline numbers — values stay numbers; the second line says what they compare to. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8, marginBottom: 14 }}>
            {statTile(tr('social.creator.stat.followers'), followersNow != null ? fmtInt(followersNow) : '—',
              growth ? tr('social.creator.stat.growthSince', { pct: `${growth.pct >= 0 ? '+' : '−'}${fmtPct(Math.abs(growth.pct), 1)}`, date: fmtShort(growth.since) }) : null,
              undefined, growth && growth.pct >= 0 ? '#5fd17a' : '#778')}
            {statTile(tr('social.creator.stat.er'), erNow != null ? fmtPct(erNow) : '—',
              bandMed != null && offer?.band ? tr('social.creator.stat.erBand', { med: fmtPct(bandMed), band: bandLabel(offer.band.lo, offer.band.hi) }) : null,
              erColor)}
            {statTile(tr('social.creator.stat.reelViews'), postsQ.isLoading ? '…' : reelViews ? compact(Math.round(reelViews.med)) : '—',
              postsQ.isLoading ? null : reelViews ? tr('social.creator.stat.reelViewsHint', { n: reelViews.n }) : tr('social.creator.stat.noReels'))}
            {statTile(tr('social.creator.stat.avgLikes'), snapshot?.avg_likes != null ? fmtInt(snapshot.avg_likes) : '—',
              snapshot?.avg_comments != null ? tr('social.creator.stat.avgCommentsHint', { n: fmtInt(snapshot.avg_comments) }) : null)}
            {statTile(tr('social.creator.stat.brandedPosts'), postsQ.isLoading ? '…' : posts90.length ? tr('social.creator.stat.nOfM', { n: branded90, m: posts90.length }) : '—',
              tr('social.creator.stat.last90'), posts90.length && branded90 / posts90.length >= 0.5 ? '#f5b700' : undefined)}
            {statTile(tr('social.creator.stat.stories30'), stories30 != null ? fmtInt(stories30) : storyActivityQ.isLoading ? '…' : '—', tr('social.creator.stat.last30'))}
          </div>

          {/* Authenticity heuristic — sudden follower spike / spike-and-revert */}
          {authFlag && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
              background: 'rgba(245,183,0,0.10)', border: '1px solid rgba(245,183,0,0.35)',
              borderRadius: 8, padding: '8px 12px', fontSize: 'var(--fs-body)', color: '#f5cf7a',
            }}>
              <NamedIcon name="warning" size={15} color="#f5b700" />
              <span>{authFlag.level === 'high' ? tr('social.creator.auth.high') : tr('social.creator.auth.watch', { pct: authFlag.pct })}</span>
            </div>
          )}

          {/* Price — the creator's list price, and a check of what the business was quoted. */}
          <div style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
            {priceFormats.length > 0 ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)' }}>{tr('social.creator.price.list')}</span>
                  {cpm != null && <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--muted)' }}>{tr('social.creator.price.cpm', { v: fmtInt(cpm) })}</span>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {priceFormats.map((k) => {
                    const on = k === fmtKey;
                    return (
                      <button key={k} type="button" onClick={() => setPriceFmt(k)}
                        style={{ fontFamily: 'inherit', fontSize: 'var(--fs-body)', cursor: 'pointer', borderRadius: 999, padding: '6px 12px', minHeight: 36,
                          background: on ? 'rgba(74,158,255,0.16)' : 'transparent', color: on ? 'var(--accent-text)' : 'var(--text-2)',
                          border: `1px solid ${on ? 'rgba(74,158,255,0.45)' : 'rgba(255,255,255,0.15)'}` }}>
                        {tr(`social.creator.price.fmt.${k}`)} · {fmtInt(Number(offer!.prices[k]!.price))} {tr('social.creator.price.som')}
                      </button>
                    );
                  })}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-caption)', color: 'var(--muted)' }}>
                  <span style={{ whiteSpace: 'nowrap' }}>{tr('social.creator.price.quoted')}</span>
                  <input type="text" inputMode="numeric" value={quoted} onChange={(e) => setQuoted(e.target.value.replace(/[^\d\s]/g, ''))}
                    placeholder={listPrice ? String(Math.round(listPrice)) : ''}
                    style={{ flex: 1, minWidth: 0, minHeight: 40, padding: '0 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.03)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 'var(--fs-body)' }} />
                  <span>{tr('social.creator.price.som')}</span>
                </label>
                <div style={{ fontSize: 'var(--fs-body)', marginTop: 6, minHeight: 20, color: verdict?.color ?? 'var(--dim)' }}>
                  {verdict?.text ?? tr('social.creator.price.hint')}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 'var(--fs-body)', color: 'var(--muted)' }}>
                {offer?.accepts_barter ? tr('social.creator.price.noneBarter') : tr('social.creator.price.none')}
              </div>
            )}
          </div>

          {/* Contact — Direct always works; the rest appear when the creator listed them. */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <a href={`https://ig.me/m/${handle}`} target="_blank" rel="noopener noreferrer" style={{ ...btn(true), flex: '1 1 180px' }}>
              <NamedIcon name="chat" size={15} color="#fff" /> {tr('social.creator.contact.direct')}
            </a>
            {contacts.whatsapp && <a href={waHref(contacts.whatsapp)} target="_blank" rel="noopener noreferrer" style={btn()}>WhatsApp</a>}
            {contacts.telegram && <a href={tgHref(contacts.telegram)} target="_blank" rel="noopener noreferrer" style={btn()}>Telegram</a>}
            {contacts.phone && <a href={`tel:${contacts.phone.replace(/[^\d+]/g, '')}`} style={btn()}>{contacts.phone}</a>}
            {contacts.email && <a href={`mailto:${contacts.email}`} style={btn()}>{contacts.email}</a>}
            <button type="button"
              onClick={() => toggleShortlist({ providerId: instagramProviderId, handle, followers: followersNow ?? 0 })}
              style={{ ...btn(), color: inShortlist(instagramProviderId) ? '#5fd17a' : 'var(--text)', borderColor: inShortlist(instagramProviderId) ? 'rgba(95,209,122,0.45)' : 'rgba(255,255,255,0.15)' }}>
              <NamedIcon name={inShortlist(instagramProviderId) ? 'check' : 'save'} size={14} />
              {inShortlist(instagramProviderId) ? tr('social.creator.list.in') : tr('social.creator.list.add')}
            </button>
          </div>
          {!contacts.whatsapp && !contacts.telegram && !contacts.phone && !contacts.email && offer && (
            <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--dim)', marginBottom: 4 }}>{tr('social.creator.contact.onlyDirect')}</div>
          )}

          {/* Tabs — scroll sideways on a phone instead of wrapping. */}
          <div role="tablist" style={{ display: 'flex', gap: 4, margin: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.08)', overflowX: 'auto', scrollbarWidth: 'none' }}>
            {([
              ['overview', tr('social.creator.tab.overview')],
              ['brands', `${tr('social.creator.tab.brands')}${card?.brand_mentions?.length ? ` · ${card.brand_mentions.length}` : ''}`],
              ['posts', `${tr('social.creator.tab.posts')} · ${posts.length}`],
              ['stories', tr('social.creator.tab.stories')],
              ['network', tr('social.creator.tab.collabs')],
            ] as const).map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
                style={{
                  flex: 'none', background: 'transparent', border: 'none', borderRadius: 0, cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 'var(--fs-body)', padding: '10px 12px', minHeight: 44, whiteSpace: 'nowrap',
                  color: tab === k ? 'var(--text)' : 'var(--muted)', boxShadow: tab === k ? 'inset 0 -2px 0 #4a9eff' : 'none',
                }}>
                {label}
              </button>
            ))}
          </div>

          {/* Overview: who they are and how the audience moves. */}
          {tab === 'overview' && (
            <div style={{ display: 'grid', gap: 16 }}>
              {card?.bio && (
                <div>
                  <div style={infoLabel}>{tr('social.creator.info.bio')}</div>
                  <div style={{ fontSize: 'var(--fs-body)', color: 'var(--text-2)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{card.bio}</div>
                </div>
              )}
              {card && ((card.ambassador_brands?.length ?? 0) + (card.ambassador_handles_raw?.length ?? 0)) > 0 && (
                <div>
                  <div style={infoLabel}>{tr('social.creator.info.ambassador')}</div>
                  <div style={chipWrap}>
                    {card.ambassador_brands.map((b) => chip(`ab-${b.id}`, b.name ?? '—', '#5fd17a'))}
                    {card.ambassador_handles_raw.map((h) => chip(`ah-${h}`, `@${h}`, '#5fd17a'))}
                  </div>
                </div>
              )}
              <div>
                <div style={infoLabel}>{tr('social.creator.chart.audience', { defaultValue: 'Аудитория' })}</div>
                {historyQ.isLoading
                  ? <Skeleton height={90} />
                  : <Suspense fallback={<Skeleton height={90} />}><CreatorAudienceSparklines points={trendPoints} labels={chartLabels.audience} locale={lang} /></Suspense>}
              </div>
              <div>
                <div style={infoLabel}>{tr('social.creator.chart.content', { defaultValue: 'Эффективность контента' })}</div>
                {postsQ.isLoading
                  ? <Skeleton height={120} />
                  : <Suspense fallback={<Skeleton height={120} />}><CreatorScatterNivo posts={posts} followers={followersNow} brandedIds={brandedPostIds} windowDays={DAYS} locale={lang} labels={chartLabels.scatter} onPick={(id) => { toggle(setPostFilter)(id); setTab('posts'); }} selectedIds={postFilter} /></Suspense>}
              </div>
              <div>
                <div style={infoLabel}>{tr('social.creator.chart.storiesActivity')}</div>
                {storyActivityQ.isLoading
                  ? <Skeleton height={90} />
                  : <Suspense fallback={<Skeleton height={90} />}><CreatorFrequencyNivo buckets={storyBuckets} barColor="#6aaa88" height={90} labels={chartLabels.stories} /></Suspense>}
              </div>
            </div>
          )}

          {/* Brands: one row per brand; a tap opens the posts with it. */}
          {tab === 'brands' && (
            (card?.brand_mentions?.length ?? 0) === 0 ? (
              <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 32 }}>{tr('social.creator.brands.none')}</div>
            ) : (
              <div>
                <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--dim)', marginBottom: 8 }}>{tr('social.creator.brands.hint')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 4 }}>
                  {(showAllBrands ? card!.brand_mentions : card!.brand_mentions.slice(0, 10)).map((m) => (
                    <button key={`bm-${m.brand_id}`} type="button" disabled={!m.name}
                      onClick={() => { if (m.name) { setBrandFilter(new Set([m.name])); setPostFilter(new Set()); setTab('posts'); } }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44, padding: '6px 10px', borderRadius: 10,
                        cursor: m.name ? 'pointer' : 'default', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
                        color: 'var(--text-2)', font: 'inherit', fontSize: 'var(--fs-body)', textAlign: 'left' }}>
                      <Avatar src={m.avatar_url} name={m.name ?? '—'} size={26} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name ?? '—'}</span>
                      <span style={{ color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>{m.count}</span>
                    </button>
                  ))}
                </div>
                {card!.brand_mentions.length > 10 && (
                  <button type="button" onClick={() => setShowAllBrands((v) => !v)}
                    style={{ marginTop: 8, background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', font: 'inherit', fontSize: 'var(--fs-body)', padding: '8px 0' }}>
                    {showAllBrands ? tr('social.creator.brands.less') : tr('social.creator.brands.more', { count: card!.brand_mentions.length - 10 })}
                  </button>
                )}
              </div>
            )
          )}
          {/* Posts grid */}
          {tab === 'posts' && (
            postsQ.isLoading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(150px, 44%), 1fr))', gap: 10 }}>
                {Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} height={180} />)}
              </div>
            ) : posts.length === 0 ? (
              <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 32 }}>{tr('social.creator.empty.noPosts')}</div>
            ) : (
              <>
                {postFilter.size > 0 ? (
                  <button type="button" onClick={() => setPostFilter(new Set())}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8, background: 'rgba(74,158,255,0.15)', border: '1px solid rgba(74,158,255,0.4)', color: 'var(--accent-text)', borderRadius: 20, padding: '3px 12px', fontSize: 'var(--fs-caption)', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <NamedIcon name="filter" size={11} /> {tr('social.creator.posts.filteredN', { count: postFilter.size, defaultValue: '{{count}} с графика' })} <NamedIcon name="x" size={11} />
                  </button>
                ) : brandFilter.size > 0 ? (
                  <button type="button" onClick={() => setBrandFilter(new Set())}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8, background: 'rgba(74,158,255,0.15)', border: '1px solid rgba(74,158,255,0.4)', color: 'var(--accent-text)', borderRadius: 20, padding: '3px 12px', fontSize: 'var(--fs-caption)', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <NamedIcon name="filter" size={11} /> {[...brandFilter].join(', ')} ·{' '}
                    {/* The rail counts mentions across posts AND stories linked to the
                        provider; the grid only holds this creator's OWN posts. Those
                        numbers differ a lot (49 vs 3 on hani_saru), so show both rather
                        than let the smaller one look like a bug. */}
                    {brandFilteredTotal > (brandPosts?.length ?? 0)
                      ? tr('social.creator.posts.filteredBrandOf', {
                          shown: brandPosts?.length ?? 0, total: brandFilteredTotal,
                          defaultValue: '{{shown}} из {{total}} — остальные в сторис и чужих постах',
                        })
                      : (brandPosts?.length ?? 0)}
                    {' '}<NamedIcon name="x" size={11} />
                  </button>
                ) : (
                  <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--dim)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {pinnedCount > 0 && <><NamedIcon name="pin" size={11} color="#556" />&nbsp;</>}
                    {tr('social.creator.posts.allPosts', { count: posts.length, defaultValue: 'Все посты · {{count}}' })}
                  </div>
                )}
                {postFilter.size === 0 && brandFilter.size === 0 && gridPosts.length > 0 && collabCount > 0 && (
                  <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                    {([['all', tr('social.creator.posts.kindAll', { defaultValue: 'Все' }), gridPosts.length],
                       ['own', tr('social.creator.posts.kindOwn', { defaultValue: 'Собственные' }), ownCount],
                       ['collab', tr('social.creator.posts.kindCollab', { defaultValue: 'Соавторство' }), collabCount]] as const).map(([k, label, n]) => (
                      <button key={k} type="button" onClick={() => setPostKind(k)}
                        style={{ fontSize: 12, padding: '5px 11px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                          background: postKind === k ? 'rgba(74,158,255,0.16)' : 'transparent',
                          border: postKind === k ? '1px solid rgba(74,158,255,0.4)' : '1px solid rgba(255,255,255,0.12)',
                          color: postKind === k ? 'var(--accent-text)' : 'var(--muted)' }}>
                        {label} · {n}
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(150px, 44%), 1fr))', gap: 10 }}>
                  {(postFilter.size > 0
                    ? posts.filter((p) => postFilter.has(p.id))
                    : brandPosts ?? kindPosts).map((p) => (
                    <PostThumb key={p.id} post={p} onOpen={() => setViewerPost(p)} />
                  ))}
                </div>
                {brandFilter.size > 0 && (brandPosts?.length ?? 0) === 0 && (
                  <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 24 }}>
                    {tr('social.creator.empty.noBrandPosts', { defaultValue: 'Нет постов с этим брендом — упоминание пришло из сторис.' })}
                  </div>
                )}
                {postFilter.size === 0 && brandFilter.size === 0 && gridPosts.length === 0 && (
                  <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 32 }}>{tr('social.creator.empty.noRecentPosts')}</div>
                )}
              </>
            )
          )}

          {/* Stories grid */}
          {tab === 'stories' && (
            storiesQ.isLoading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
                {Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} height={220} />)}
              </div>
            ) : stories.length === 0 ? (
              <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 32 }}>{tr('social.creator.empty.noStories')}</div>
            ) : (
              <>
                {storyFilter.size > 0 ? (
                  <button type="button" onClick={() => setStoryFilter(new Set())}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8, background: 'rgba(74,158,255,0.15)', border: '1px solid rgba(74,158,255,0.4)', color: 'var(--accent-text)', borderRadius: 20, padding: '3px 12px', fontSize: 'var(--fs-caption)', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <NamedIcon name="filter" size={11} /> {tr('social.creator.posts.filteredDays', { count: storyFilter.size, defaultValue: '{{count}} дн. с графика' })} <NamedIcon name="x" size={11} />
                  </button>
                ) : brandFilter.size > 0 ? (
                  <button type="button" onClick={() => setBrandFilter(new Set())}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8, background: 'rgba(74,158,255,0.15)', border: '1px solid rgba(74,158,255,0.4)', color: 'var(--accent-text)', borderRadius: 20, padding: '3px 12px', fontSize: 'var(--fs-caption)', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <NamedIcon name="filter" size={11} /> {[...brandFilter].join(', ')} · {brandStoriesQ.isLoading ? '…' : tr('social.creator.posts.ofStoriesWithBrand', { count: brandStories?.length ?? 0, defaultValue: '{{count}} сторис' })} <NamedIcon name="x" size={11} />
                  </button>
                ) : (
                  <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--dim)', marginBottom: 8 }}>
                    {tr('social.creator.posts.recent60')} · {tr('social.creator.posts.ofStories', { shown: recentStories.length, total: stories.length })}
                  </div>
                )}
                {brandFilter.size > 0 && storyFilter.size === 0 && brandStoriesQ.isLoading ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
                    {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={220} />)}
                  </div>
                ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
                  {(storyFilter.size > 0
                    ? stories.filter((s) => s.taken_at && storyFilter.has(localDay(new Date(s.taken_at))))
                    : brandStories ?? recentStories).map((s) => (
                    <StoryThumb key={s.id} story={s} brands={brandStoryBadges.get(s.id) ?? storyBrands.get(s.id)} onOpen={() => setViewerStory(s)} />
                  ))}
                </div>
                )}
                {storyFilter.size === 0 && brandFilter.size > 0 && !brandStoriesQ.isLoading && (brandStories?.length ?? 0) === 0 && (
                  <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 24 }}>
                    {tr('social.creator.empty.noBrandStories', { defaultValue: 'Нет сторис с этим брендом.' })}
                  </div>
                )}
                {storyFilter.size === 0 && brandFilter.size === 0 && recentStories.length === 0 && (
                  <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 32 }}>{tr('social.creator.empty.noRecentStories')}</div>
                )}
              </>
            )
          )}

          {/* Collaboration network */}
          {tab === 'network' && (
            <CollabSection
              providerId={instagramProviderId}
              handle={handle}
              followers={followers}
              onOpenCreator={onOpenCreator}
            />
          )}
        </SectionErrorBoundary>
      </div>
      </div>
      {viewerStory && <CustomerStoryViewer story={viewerStory} brands={storyBrands.get(viewerStory.id)} onClose={() => setViewerStory(null)} />}
      {viewerPost && (
        <PostViewerModal
          post={{
            id: viewerPost.id,
            post_type: viewerPost.post_type,
            thumb_url: viewerPost.thumb_public_url || viewerPost.thumbnail_url,
            video_url: viewerPost.video_public_url,
            caption: viewerPost.caption,
            likes: viewerPost.likes,
            comments: viewerPost.comments,
            posted_at: viewerPost.posted_at,
            permalink: viewerPost.permalink,
            handle,
            advertiser_name: (viewerPost.brands ?? []).map((b) => b.name).join(', ') || null,
            // Without this the viewer sees no slides and shows only the cover — a
            // carousel looked like a single image here while the brand card paged
            // through it correctly. get_creator_grid_posts has returned child_posts
            // all along; this surface simply never forwarded them.
            child_posts: viewerPost.child_posts,
          }}
          onClose={() => setViewerPost(null)}
        />
      )}
    </>,
    document.body,
  );
}

function PostThumb({ post, onOpen }: { post: PostRow; onOpen: () => void }) {
  const { t: tr } = useTranslation('customer');
  const [hover, setHover] = useState(false);
  const thumb = post.thumb_public_url || post.thumbnail_url;
  const isVideo = post.post_type === 'reel' || post.post_type === 'video';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      style={{ cursor: 'pointer', display: 'block' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={{ position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', background: '#1a1d28', border: '1px solid rgba(255,255,255,0.06)' }}>
        {thumb ? (
          <SignedImg src={thumb} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'opacity 0.15s', opacity: hover ? 0.6 : 1 }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#445' }}><NamedIcon name="camera" size={28} color="#445" /></div>
        )}

        {isVideo && (
          <div style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(0,0,0,0.7)', borderRadius: 4, padding: '3px 5px', display: 'flex', color: 'var(--accent-text)' }}>
            <NamedIcon name={post.post_type === 'reel' ? 'film' : 'play'} size={11} color="#bcd0ff" />
          </div>
        )}
        {post.is_paid_partnership && (
          <div style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(245,183,0,0.85)', borderRadius: 4, padding: '1px 5px', fontSize: 'var(--fs-micro)', color: '#000', fontWeight: 'var(--fw-bold)' }}>AD</div>
        )}
        {post.is_pinned && (
          <div title={tr('social.creator.pinnedPost')} style={{ position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.7)', borderRadius: 4, padding: '3px 5px', display: 'flex', color: '#ffd479' }}><NamedIcon name="pin" size={11} color="#ffd479" /></div>
        )}
        {post.is_coauthored && (
          <div title={post.owner_handle ? `Collab · @${post.owner_handle}` : 'Collaboration'}
            style={{ position: 'absolute', bottom: 6, left: 6, background: 'rgba(80,200,120,0.85)', borderRadius: 4, padding: '2px 6px', fontSize: 'var(--fs-micro)', color: '#062', fontWeight: 'var(--fw-bold)', display: 'flex', alignItems: 'center', gap: 3 }}>
            <NamedIcon name="handshake" size={11} color="#062" /> {post.owner_handle ? `@${post.owner_handle}` : 'collab'}
          </div>
        )}
        {post.brands && post.brands.length > 0 && (
          <div style={{ position: 'absolute', left: 4, right: 4, bottom: 4, display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', pointerEvents: 'none' }}>
            {post.brands.slice(0, 2).map((b, i) => (
              <span key={i}
                title={b.ocr ? tr('social.creator.brandOcr', { defaultValue: 'Реклама в баннере (распознано с картинки)' }) : tr('social.creator.brandMention', { defaultValue: 'Упоминание бренда' })}
                style={{
                  fontSize: 'var(--fs-micro)', lineHeight: 1.4, padding: '1px 6px', borderRadius: 999,
                  background: b.ocr ? 'rgba(245,183,0,0.92)' : 'rgba(10,14,24,0.82)',
                  color: b.ocr ? '#000' : '#cfe0ff',
                  border: b.ocr ? 'none' : '1px solid rgba(120,150,220,0.4)',
                  fontWeight: b.ocr ? 'var(--fw-bold)' : 'var(--fw-semibold)',
                  maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{b.name}</span>
            ))}
            {post.brands.length > 2 && (
              <span style={{ fontSize: 'var(--fs-micro)', padding: '1px 5px', borderRadius: 999, background: 'rgba(10,14,24,0.82)', color: 'var(--muted)' }}>+{post.brands.length - 2}</span>
            )}
          </div>
        )}

        {hover && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)', transition: 'opacity 0.15s' }} />
        )}
      </div>

      {/* Date, caption and engagement live BELOW the image, always visible — the same
          shape the brand-mentions card uses. They used to sit in a hover overlay, which
          meant reading a grid one tile at a time and showed nothing at all on touch.
          The caption keeps a reserved two-line height so tiles stay flush in the grid. */}
      <div style={{ padding: '6px 2px 0', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '2px 6px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--dim)', whiteSpace: 'nowrap' }}>{post.posted_at ? fmtShort(post.posted_at) : '—'}</span>
          <span style={{ display: 'inline-flex', gap: 8, fontSize: 'var(--fs-caption)', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
            {post.likes != null && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><NamedIcon name="heart" size={11} color="#9aa" /> {compact(post.likes)}</span>}
            {post.comments != null && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><NamedIcon name="chat" size={11} color="#9aa" /> {compact(post.comments)}</span>}
            {post.views != null && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><NamedIcon name="eye" size={11} color="#9aa" /> {compact(post.views)}</span>}
          </span>
        </div>
        <div style={{
          fontSize: 'var(--fs-small)', color: 'var(--text-2)', lineHeight: 1.4, minHeight: '2.8em',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>{post.caption ?? ''}</div>
      </div>
    </div>
  );
}

function StoryThumb({ story, brands, onOpen }: { story: StoryRow; brands?: string[]; onOpen?: () => void }) {
  const [hover, setHover] = useState(false);
  const thumb = story.thumb_public_url || story.thumbnail_url || story.media_url;
  const isVideo = (story.media_type ?? '').toLowerCase() === 'video';

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onOpen}
      style={{ cursor: onOpen ? 'pointer' : 'default' }}
    >
      <div style={{ position: 'relative', aspectRatio: '9/16', borderRadius: 8, overflow: 'hidden', background: '#1a1d28', border: '1px solid rgba(255,255,255,0.06)' }}>
        {thumb ? (
          <SignedImg src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'opacity 0.15s', opacity: hover ? 0.7 : 1 }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#445' }}><NamedIcon name="clock" size={24} color="#445" /></div>
        )}
        {isVideo && <div style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(0,0,0,0.7)', borderRadius: 4, padding: '3px 5px', display: 'flex', color: 'var(--accent-text)' }}><NamedIcon name="play" size={11} color="#bcd0ff" /></div>}
        {brands && brands.length > 0 && (
          <div style={{ position: 'absolute', top: 6, right: 6, maxWidth: '80%', background: 'rgba(245,183,0,0.9)', borderRadius: 4, padding: '2px 6px', fontSize: 'var(--fs-micro)', color: '#3a2a00', fontWeight: 'var(--fw-bold)', display: 'flex', alignItems: 'center', gap: 3, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            <NamedIcon name="star" size={10} color="#3a2a00" /> {brands[0]}{brands.length > 1 ? ` +${brands.length - 1}` : ''}
          </div>
        )}
        {hover && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }} />}
      </div>
      {/* Same reasoning as the post tile: the date belongs under the frame, not behind a
          hover that a touch device can never trigger. Stories carry no likes or comments. */}
      <div style={{ padding: '5px 2px 0', fontSize: 'var(--fs-caption)', color: 'var(--dim)' }}>
        {story.taken_at ? fmtDate(story.taken_at) : '—'}
      </div>
    </div>
  );
}

// Fullscreen story viewer — plays the mirrored video when available, else the cover.
function CustomerStoryViewer({ story, brands, onClose }: { story: StoryRow; brands?: string[]; onClose: () => void }) {
  const { t: tr } = useTranslation('customer');
  const [videoErrored, setVideoErrored] = useState(false);
  const [imgErrored, setImgErrored] = useState(false);
  const isVideo = (story.media_type ?? '').toLowerCase() === 'video';
  const poster = story.thumb_public_url || story.thumbnail_url || undefined;
  const canPlayVideo = isVideo && !!story.media_url && !videoErrored;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 2147483647, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', width: 36, height: 36, borderRadius: 18, cursor: 'pointer', fontSize: 'var(--fs-h2)', lineHeight: 1, fontFamily: 'inherit' }}>×</button>
      <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, maxWidth: '92vw' }}>
        {canPlayVideo ? (
          <SignedVideo src={story.media_url!} poster={poster} autoPlay controls loop onError={() => setVideoErrored(true)}
            style={{ maxWidth: '92vw', maxHeight: '78vh', borderRadius: 10, background: '#111', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }} />
        ) : (poster && !imgErrored) ? (
          <SignedImg src={poster} alt="" onError={() => setImgErrored(true)}
            style={{ maxWidth: '92vw', maxHeight: '78vh', objectFit: 'contain', borderRadius: 10, background: '#111', boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }} />
        ) : (
          <div style={{ maxWidth: 380, padding: 24, borderRadius: 10, background: 'rgba(20,22,30,0.95)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--muted)', textAlign: 'center', fontFamily: 'inherit' }}>
            {tr('social.creator.story.unavailable', { defaultValue: 'Сторис больше недоступна' })}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {brands && brands.length > 0 && (
            <span style={{ background: 'rgba(245,183,0,0.9)', color: '#3a2a00', fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-caption)', borderRadius: 20, padding: '3px 12px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <NamedIcon name="star" size={11} color="#3a2a00" /> {brands.join(', ')}
            </span>
          )}
          {story.taken_at && <span style={{ color: 'var(--muted)', fontSize: 'var(--fs-caption)' }}>{fmtDate(story.taken_at)}</span>}
        </div>
      </div>
    </div>
  );
}
